import { PeriodTypeEnum, RegisterCompositionPeriod, TimetableStructure } from '@schooly/api';

import {
  timeToMinutes,
  WEEKDAY_TO_DAY_INDEX,
} from '../../../School/SchoolTimetableStructures/utils';
import {
  RegisterPeriod,
  RegisterPeriodStructure,
  RegisterStructuresWithPeriods,
  toRegisterSession,
} from '../types';

export const hasPublishedTimetableForYear = (
  structure: Pick<TimetableStructure, 'timetable_usage'>,
  schoolYearId: string,
) =>
  structure.timetable_usage.some(
    (usage) => usage.school_year_id === schoolYearId && usage.has_published,
  );

export const getLessonSlotsOnDate = (
  structure: Pick<TimetableStructure, 'periods' | 'time_slots'>,
  date: Date,
) => {
  const dayIndex = date.getDay();
  const periodsById = new Map(structure.periods.map((period) => [period.id, period]));

  return structure.time_slots
    .filter((slot) => {
      const period = periodsById.get(slot.period_id);
      return (
        period &&
        period.type === PeriodTypeEnum.Lesson &&
        WEEKDAY_TO_DAY_INDEX[slot.weekday] === dayIndex
      );
    })
    .map((slot) => {
      const period = periodsById.get(slot.period_id);
      return {
        name: period?.name ?? '',
        id: period?.id ?? '',
        start_time: slot.start_time,
        end_time: slot.end_time,
        register_session: toRegisterSession(period?.register_session),
      };
    })
    .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
};

export const emptyRegisterStructuresWithPeriods = (): RegisterStructuresWithPeriods => ({
  structures: [],
  periodsByStructureId: {},
});

export const getRegisterStructuresWithPeriods = (
  structures: TimetableStructure[],
  date: Date,
  schoolYearId: string,
): RegisterStructuresWithPeriods => {
  const periodsByStructureId: Record<string, RegisterPeriod[]> = {};
  const filteredStructures: RegisterPeriodStructure[] = [];

  for (const structure of structures) {
    if (!hasPublishedTimetableForYear(structure, schoolYearId)) continue;

    const lessons = getLessonSlotsOnDate(structure, date);
    if (!lessons.length) continue;

    filteredStructures.push({ id: structure.id, name: structure.name });
    periodsByStructureId[structure.id] = lessons.map((lesson, index) => ({
      ...lesson,
      order: index + 1,
      structure: { id: structure.id, name: structure.name },
    }));
  }

  return { structures: filteredStructures, periodsByStructureId };
};

export const getRegisterStructuresFromComposition = (
  periods: RegisterCompositionPeriod[],
): RegisterStructuresWithPeriods => {
  if (!periods.length) return emptyRegisterStructuresWithPeriods();

  const periodsByStructureId: Record<string, RegisterPeriod[]> = {};
  const structures: RegisterPeriodStructure[] = [];

  for (const period of periods) {
    if (period.type !== PeriodTypeEnum.Lesson) continue;

    const structureId = period.timetable_structure.id;
    if (!periodsByStructureId[structureId]) {
      structures.push(period.timetable_structure);
      periodsByStructureId[structureId] = [];
    }

    periodsByStructureId[structureId].push({
      id: period.id,
      name: period.name,
      start_time: period.start_time,
      end_time: period.end_time,
      register_session: toRegisterSession(period.register_session),
      structure: period.timetable_structure,
      order: 0,
    });
  }

  for (const structurePeriods of Object.values(periodsByStructureId)) {
    structurePeriods.sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
    structurePeriods.forEach((period, index) => (period.order = index + 1));
  }

  return { structures, periodsByStructureId };
};
